import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { LEVEL_SEED_QUESTIONS } from '@/constants/levelSeed';
import type { LevelSeedSelectorHandle, LevelSeedSelectorProps } from './LevelSeedSelector.types';
import styles from './LevelSeedSelector.module.scss';

/**
 * Selector de una pregunta de autoevaluación "can-do" (sembrado de nivel).
 * Reusa el mismo estilo de cards que LevelSelector. Expone el peso elegido vía
 * ref imperativa para que el onboarding combine las 2 respuestas y fije el nivel.
 */
export const LevelSeedSelector = forwardRef<LevelSeedSelectorHandle, LevelSeedSelectorProps>(
  function LevelSeedSelector({ questionIndex, disabled = false, onValidityChange }, ref) {
    const { t } = useTranslation();
    const question = LEVEL_SEED_QUESTIONS[questionIndex]!;
    const [selected, setSelected] = useState<number | null>(null);

    const lastNotifiedValidRef = useRef<boolean | null>(null);
    useEffect(() => {
      const isValid = selected !== null;
      if (lastNotifiedValidRef.current === isValid) return;
      lastNotifiedValidRef.current = isValid;
      onValidityChange?.(isValid);
    }, [selected, onValidityChange]);

    useImperativeHandle(
      ref,
      () => ({
        getWeight: () => (selected !== null ? question.options[selected]!.weight : null),
        isValid: () => selected !== null,
      }),
      [selected, question]
    );

    return (
      <RadioGroupPrimitive.Root
        className={styles.seed__list}
        value={selected !== null ? String(selected) : undefined}
        onValueChange={(v) => setSelected(Number(v))}
        disabled={disabled}
        aria-label={t(question.questionKey)}
      >
        {question.options.map((option, idx) => {
          const id = `seed-${questionIndex}-${idx}`;
          return (
            <label key={id} htmlFor={id} className={styles.seed__card}>
              <RadioGroupPrimitive.Item
                value={String(idx)}
                id={id}
                className={styles.seed__radio}
                data-no-press-scale=""
              >
                <RadioGroupPrimitive.Indicator className={styles.seed__indicator} />
              </RadioGroupPrimitive.Item>
              <span className={styles.seed__label}>{t(option.labelKey)}</span>
            </label>
          );
        })}
      </RadioGroupPrimitive.Root>
    );
  }
);

LevelSeedSelector.displayName = 'LevelSeedSelector';
